import {
  Alert,
  Attributes,
  AttributesRow,
  Button,
  ButtonGroup,
  Checkbox,
  NumberInput,
} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useCorrectVehicleMileageMutation, useGetVehicleQuery} from '@dms/api/sales';
import {
  CarPassRequestIssues,
  useConfirmCarPassRequestMutation,
  useCorrectCarPassRequestMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {getCarPassMileageIssueKeys} from '../utils/getCarPassMileageIssueKeys';
import {CarPassSubmitSuccessContent} from './CarPassSubmitSuccessContent';

export interface CarPassActionRequiredDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  requestId: string;
  issues: CarPassRequestIssues | null;
}

export function CarPassActionRequiredDialog(props: CarPassActionRequiredDialogProps) {
  const formatNumber = useFormatNumber();
  const [isConsented, setIsConsented] = useState(false);
  const [isEditingMileage, openMileageEdit, closeMileageEdit] = useBoolean();
  const [isSubmitted, setSubmitted] = useBoolean();
  const [mileageInputValue, setMileageInputValue] = useState<number | null>(null);

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const currentMileage = vehicle?.state?.mileage ?? null;

  const [confirmCarPassRequest, {isLoading: isConfirming}] = useConfirmCarPassRequestMutation();
  const [correctCarPassRequest, {isLoading: isCorrecting}] = useCorrectCarPassRequestMutation();
  const [correctVehicleMileage, {isLoading: isSavingMileage}] = useCorrectVehicleMileageMutation();

  const mileageIssueKeys = getCarPassMileageIssueKeys(props.issues);

  // Confirm-as-is: the entered mileage is correct, so tell Car-Pass to accept it.
  const handleConfirm = () => {
    confirmCarPassRequest({vehicleId: props.vehicleId, requestId: props.requestId})
      .unwrap()
      .then(setSubmitted)
      .catch(handleApiError);
  };

  const handleOpenEditMileage = () => {
    openMileageEdit();
    setMileageInputValue(currentMileage);
  };

  const resetMileageEditor = () => {
    closeMileageEdit();
    setMileageInputValue(null);
  };

  /**
   * Correction path: persist the new mileage on the vehicle, then submit the
   * correction to Car-Pass in a single action — no session flag deciding which
   * endpoint to call later.
   *
   * TODO Q-BE-6 (residual): if `correctVehicleMileage` succeeds but
   * `correctCarPassRequest` then fails, the vehicle holds the corrected value
   * while Car-Pass still has the old one; on reopen "Confirm" would wrongly call
   * `/confirm`. The full fix is to derive confirm-vs-correct from the Car-Pass
   * mileage once the backend exposes it.
   */
  const handleSaveAndCorrect = () => {
    if (isNil(mileageInputValue)) {
      return;
    }

    correctVehicleMileage({
      vehicleId: props.vehicleId,
      correctMileageRequestBody: {mileage: mileageInputValue, note: null},
    })
      .unwrap()
      .then(() =>
        correctCarPassRequest({
          vehicleId: props.vehicleId,
          requestId: props.requestId,
        }).unwrap()
      )
      .then(setSubmitted)
      .catch(handleApiError);
  };

  const mileageRow: AttributesRow = isEditingMileage
    ? {
        label: i18n.t('page.carPass.labels.currentMileage'),
        content: (
          <NumberInput
            value={mileageInputValue}
            onChange={setMileageInputValue}
            suffix={i18n.t('general.metric.km')}
            data-testid={suffixTestId('mileageInput', props)}
          />
        ),
      }
    : {
        label: i18n.t('page.carPass.labels.enteredMileage'),
        value: isNotNil(currentMileage)
          ? `${formatNumber(currentMileage)} ${i18n.t('general.metric.km')}`
          : EMPTY_PLACEHOLDER,
      };

  if (isSubmitted) {
    return (
      <CarPassSubmitSuccessContent
        vehicleTitle={vehicle?.title}
        mileage={currentMileage}
        data-testid={suffixTestId('success', props)}
      />
    );
  }

  return (
    <VStack spacing={4}>
      <Attributes rows={[mileageRow]} data-testid={suffixTestId('mileage', props)} />

      <VStack spacing={3}>
        {mileageIssueKeys.map((messageKey) => (
          <Alert
            key={messageKey}
            variant="warning"
            title={i18n.t(messageKey)}
            data-testid={suffixTestId(`issue-${messageKey}`, props)}
          />
        ))}
      </VStack>

      <Show when={!isEditingMileage}>
        <Text size="small" color="secondary">
          {i18n.t('page.carPass.labels.actionRequiredInstruction')}
        </Text>

        <Checkbox
          value={isConsented}
          onChange={setIsConsented}
          label={i18n.t('page.carPass.labels.actionRequiredConsent', {
            mileage: formatNumber(currentMileage),
          })}
          data-testid={suffixTestId('consent', props)}
        />
      </Show>

      <ButtonGroup align="right">
        <Show when={isEditingMileage}>
          <Button
            variant="secondary"
            title={i18n.t('general.actions.cancel')}
            isDisabled={isSavingMileage || isCorrecting}
            onClick={resetMileageEditor}
            data-testid={suffixTestId('cancelMileageEdit', props)}
          />
          <Button
            variant="primary"
            title={i18n.t('page.carPass.actions.saveAndSend')}
            isLoading={isSavingMileage || isCorrecting}
            isDisabled={isNil(mileageInputValue)}
            onClick={handleSaveAndCorrect}
            data-testid={suffixTestId('saveMileage', props)}
          />
        </Show>

        <Show when={!isEditingMileage}>
          <Button
            variant="secondary"
            leftIcon="image/edit"
            title={i18n.t('page.carPass.actions.editMileage')}
            isDisabled={isConfirming}
            onClick={handleOpenEditMileage}
            data-testid={suffixTestId('editMileage', props)}
          />
          <Button
            variant="primary"
            leftIcon="action/check_circle"
            title={i18n.t('page.carPass.actions.confirmAndSend')}
            isDisabled={!isConsented}
            isLoading={isConfirming}
            onClick={handleConfirm}
            data-testid={suffixTestId('confirm', props)}
          />
        </Show>
      </ButtonGroup>
    </VStack>
  );
}
